import { useEffect, useRef } from "react";
import { useAuth, useI18N, useOnboardingResult } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import Loader from "@/components/common/Loader";
import { useAtom } from "jotai";
import {
  onboardingCompletedAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
} from "@/store/atoms";
import { useCustomToast } from "@/hooks/useCustomToast";
import { bucketUrlPrefix } from "common/config";
import Modal from "react-responsive-modal";
import Slider from "react-slick";
import { useRouter } from "@tanstack/react-router";
import { useDarkMode } from "@/hooks/useDarkMode";
import { StandardButton } from "@/components/common/StandardButton";

const SafeSlider = Slider as any;

function SamplePrevArrow(props: any) {
  const { onClick } = props;
  const i18n = useI18N();
  return (
    <div
      role="button"
      tabIndex={0} // Make it focusable
      className="cursor-pointer w-[280px] px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 inline-flex"
      onClick={onClick} // Apply the click handler to the entire div
    >
      <div className="text-center text-fg-neutral-strong title-s-semibold leading-normal">
        {i18n.get("previous")}
      </div>
    </div>
  );
}

function SampleNextArrow(props: any) {
  const { onClick } = props;
  const i18n = useI18N();
  return (
    <div
      role="button"
      tabIndex={0} // Make it focusable
      className="cursor-pointer w-[280px] px-6 py-3 bg-fg-neutral-strong rounded-xl justify-center items-center gap-3 inline-flex"
      onClick={onClick} // Apply the click handler to the entire div
    >
      <div className="text-center text-white title-s-semibold leading-normal">
        {i18n.get("next")}
      </div>
    </div>
  );
}

function shouldOpenOnboardingModal(
  initialRoute: string | string[],
  queryParams: URLSearchParams,
) {
  const excludedRoutes = ["/passwordreset", "/verifyuser", "/venuedetails"];
  const excludedQueryParams = ["utm_source", "utm_medium", "utm_term"];

  const isRouteExcluded = excludedRoutes.some((route) =>
    initialRoute.includes(route),
  );
  const isQueryExcluded = excludedQueryParams.some((param) =>
    queryParams.has(param),
  );

  return !(isRouteExcluded || isQueryExcluded);
}

interface ModalProps {
  onClose: () => void;
}

function OnboardingScreensComponent(props: ModalProps) {
  const { onClose } = props;
  const i18n = useI18N();
  const { user } = useAuth();
  const showToast = useCustomToast();
  const isDark = useDarkMode();
  const { data, error, isLoading } = useOnboardingResult({
    isDark,
    isMobile: false,
  });
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useAtom(
    onboardingCompletedAtom,
  );
  const sliderRef = useRef<Slider | null>(null);
  const [isOnboardingModalOpen, setIsOnboardingModalOpen] =
    useAtom(onboardingScreenAtom);
  const [onboardingCurrentScreen, setOnboardingCurrentScreen] = useAtom(
    onboardingCurrentScreenAtom,
  );

  const totalSlides = data?.photos?.length || 0;

  const router = useRouter();
  const initialRouteRef = useRef(null);

  useEffect(() => {
    if (data && !isOnboardingCompleted) {
      AnalyticsService.trackGAEventHandler("homeOnboardingSliderStarted");
      AnalyticsService.trackMx("homeOnboardingSliderStarted", {});

      console.log("open onboarding modal");
      setIsOnboardingModalOpen(true);
      setIsOnboardingCompleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isOnboardingCompleted]);

  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Tab" && !e.shiftKey) {
        // Move to the next slide on Tab
        sliderRef.current?.slickNext();
      } else if (e.key === "Tab" && e.shiftKey) {
        // Move to the previous slide on Shift + Tab
        sliderRef.current?.slickPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [sliderRef]);

  const handleFinishOnboarding = () => {
    console.log("handleFinishOnboarding");

    AnalyticsService.trackGAEventHandler("homeOnboardingSliderCompleted");
    AnalyticsService.trackMx("homeOnboardingSliderCompleted", {
      UserName: user?.name,
    });

    setIsOnboardingCompleted(true);
    setIsOnboardingModalOpen(false);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  // Store the initial route when the component mounts
  useEffect(() => {
    if (!initialRouteRef.current) {
      initialRouteRef.current = router.state.location.pathname;
    }
  }, [router.state.location.pathname]);

  const queryParams = new URLSearchParams(
    Object.entries(router.state.location.search).map(([key, value]) => [
      key,
      String(value),
    ]),
  );
  const shouldOpenModal =
    isOnboardingModalOpen &&
    shouldOpenOnboardingModal(initialRouteRef.current, queryParams);

  return (
    <>
      {isLoading && <Loader />}
      <Modal
        focusTrapped
        open={shouldOpenModal}
        onClose={onClose}
        classNames={{
          root: "",
          modal: "imagelistviewer-modal",
        }}
        styles={{
          modal: {
            maxWidth: "1200px", // Override default 800px
            width: "100%", // Make it responsive
            maxHeight: "90vh",
          },
        }}
      >
        <div className="w-full max-w-[1200px] h-auto md:max-h-[90vh] pb-4 bg-bg-brand-secondary-default rounded-[16px] overflow-hidden">
          <div className="p-3 w-full md:w-[800px] lg:w-[1200px] h-[100%]">
            <SafeSlider
              ref={sliderRef}
              dots
              dotsClass={`slick-dots custom-dots ${isDark ? "dark-mode-dots" : ""}`}
              afterChange={(index) => {
                AnalyticsService.trackGAEventHandler(
                  "homeOnboardingSliderSlide",
                );
                AnalyticsService.trackMx("homeOnboardingSliderSlide", {
                  sliderSlideNumber: index,
                });

                setOnboardingCurrentScreen(index);
              }}
              initialSlide={onboardingCurrentScreen}
              nextArrow={null}
              prevArrow={null}
              infinite={false}
              arrows={false}
              customPaging={() => (
                <button aria-hidden="true" tabIndex={-1}></button> // Disable dots focus
              )}
              accessibility={false} // Disable slider keyboard navigation
            >
              {data?.photos?.map((photo, index) => (
                <div
                  key={index}
                  className="w-[100%] h-auto"
                  aria-label={`Onboarding screen ${index + 1}`}
                  tabIndex={-1} // Prevent Tab focus on the slide container
                  role="group" // Define the slide as a group of content
                  aria-roledescription="slide" // Describe the content as a slide
                  aria-labelledby={`slide-title-${index}`} // Link to the title
                  aria-describedby={`slide-description-${index}`} // Link to the description
                >
                  <div className="flex-col justify-start items-center gap-2 flex mt-5 px-4 md:px-8">
                    <div
                      id={`slide-title-${index}`} // Title is linked for accessibility
                      className="text-center text-fg-neutral-strong title-s-semibold md:title-m-bold lg:title-m-bold"
                      tabIndex={-1} // Prevent focus
                      aria-live="polite" // Screen reader announces this when it changes
                    >
                      {photo.title}
                    </div>
                    <div
                      id={`slide-description-${index}`} // Description is linked for accessibility
                      className="text-center text-fg-neutral-subtle label-m-medium mt-1 mb-4"
                      tabIndex={-1} // Prevent focus
                      aria-live="polite" // Screen reader announces this when it changes
                    >
                      {photo.description}
                    </div>
                  </div>
                  <div className="relative px-2 md:px-6">
                    <img
                      aria-hidden="true"
                      src={bucketUrlPrefix + photo.image}
                      alt={photo.alt_text}
                      className="rounded-[16px] object-contain max-h-[300px] md:max-h-[40vh] min-h-[200px] md:min-h-[50vh] w-full h-auto"
                    />
                  </div>
                </div>
              ))}
            </SafeSlider>
          </div>

          {/* Previous, Next, or Finish Button */}
          <div className="flex justify-center items-center gap-4 mt-6 mb-2 ml-2 mr-2">
            {onboardingCurrentScreen < totalSlides - 1 ? (
              <>
                {onboardingCurrentScreen > 0 && (
                  <SamplePrevArrow
                    onClick={() => sliderRef.current?.slickPrev()}
                  />
                )}
                <SampleNextArrow
                  onClick={() => sliderRef.current?.slickNext()}
                />
              </>
            ) : (
              <>
                <SamplePrevArrow
                  onClick={() => sliderRef.current?.slickPrev()}
                />
                <StandardButton
                  variant="primary"
                  className="cursor-pointer w-[280px] px-6 py-3 bg-fg-neutral-strong text-white rounded-xl justify-center items-center gap-3 inline-flex"
                  onClick={() => handleFinishOnboarding()}
                >
                  <div className="text-center text-white title-s-semibold leading-normal">
                    {i18n.get("startExploring")}
                  </div>
                </StandardButton>
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

export default OnboardingScreensComponent;
